import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Chip,
  TextField,
  InputAdornment,
  Avatar,
  IconButton,
  Tooltip,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import GroupsIcon from "@mui/icons-material/Groups";
import EditNoteIcon from "@mui/icons-material/EditNote";
import SearchIcon from "@mui/icons-material/Search";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ShareIcon from "@mui/icons-material/Share";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import StarIcon from "@mui/icons-material/Star";
import ChatIcon from "@mui/icons-material/Chat";
import { getSharedNotes } from "../../firebase/notesService";
import { useSocial } from "../../context/SocialContext";
import { chapterPathWithVerse, cleanVerseQuoteText, formatBookName } from "../../bible/verseQuote";

const CommunityNotesPage = () => {
  const navigate = useNavigate();
  const { openUserCard, openChatWithUser } = useSocial();
  const [sharedNotes, setSharedNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTag, setSelectedTag] = useState("all");
  const [toastMsg, setToastMsg] = useState("");

  const fetchCommunityNotes = useCallback(async ({ silent = false, bypassCache = false } = {}) => {
    if (!silent) setLoading(true);
    const list = await getSharedNotes({ max: 50, bypassCache });
    setSharedNotes(list);
    if (!silent) setLoading(false);
  }, []);

  useEffect(() => {
    fetchCommunityNotes({ bypassCache: true });
    const refetch = () => fetchCommunityNotes({ silent: true, bypassCache: true });
    window.addEventListener("bible_notes_changed", refetch);
    window.addEventListener("bible_shared_notes_changed", refetch);
    window.addEventListener("focus", refetch);
    return () => {
      window.removeEventListener("bible_notes_changed", refetch);
      window.removeEventListener("bible_shared_notes_changed", refetch);
      window.removeEventListener("focus", refetch);
    };
  }, [fetchCommunityNotes]);

  const allTags = useMemo(() => {
    const set = new Set();
    sharedNotes.forEach((n) => {
      (n.tags || []).forEach((t) => set.add(t));
    });
    return Array.from(set);
  }, [sharedNotes]);

  const filteredNotes = useMemo(() => {
    return sharedNotes.filter((n) => {
      if (selectedTag !== "all" && !(n.tags || []).includes(selectedTag))
        return false;

      if (searchTerm.trim()) {
        const query = searchTerm.toLowerCase();
        const matchTitle = (n.title || "").toLowerCase().includes(query);
        const matchContent = (n.content || "").toLowerCase().includes(query);
        const matchAuthor = (n.userDisplayName || "").toLowerCase().includes(query);
        const matchBook = (n.bookName || "").toLowerCase().includes(query);
        const matchVerse = (n.verseText || "").toLowerCase().includes(query);
        return matchTitle || matchContent || matchAuthor || matchBook || matchVerse;
      }
      return true;
    });
  }, [sharedNotes, selectedTag, searchTerm]);

  const handleCopyNote = (note) => {
    const text = `【${note.title}】\n经文：${formatBookName(note.bookName)} 第${note.chapterNumber}章${
      note.verseRange ? `:${note.verseRange}` : ""
    }\n摘录：${cleanVerseQuoteText(note.verseText)}\n心得：${note.content}\n—— 分享自读经读者：${
      note.userDisplayName || "主内读者"
    }`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setToastMsg("经文与灵修笔记已复制！");
    }
  };

  const handleCopyLink = (note) => {
    const url = `${window.location.origin}/shared/notes/${note.id}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
      setToastMsg("分享链接已复制到剪贴板！");
    }
  };

  const handleReport = () => {
    setToastMsg("感谢您的反馈，已将该笔记提交给管理员审核！");
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, textAlign: "center" }}>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1.5,
            mb: 1,
            justifyContent: "center",
          }}
        >
          <GroupsIcon color="primary" sx={{ fontSize: 40 }} />
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            社区圣经灵修分享墙
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          彼此勉励，共同在神的话语中扎根成长（公开发布的灵修笔记）
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 3,
          border: "1px dashed",
          borderColor: "primary.light",
          display: "flex",
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 1.5,
          flexDirection: { xs: "column", sm: "row" },
          bgcolor: (theme) =>
            theme.palette.mode === "dark" ? "rgba(25, 118, 210, 0.06)" : "rgba(25, 118, 210, 0.03)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
          <EditNoteIcon color="primary" sx={{ mt: 0.25 }} />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              如何在分享墙发帖？
            </Typography>
            <Typography variant="body2" color="text.secondary">
              社区没有单独的发帖框。请到「我的笔记」写下一则灵修，再将它设为公开，就会出现在这里。
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate("/notes")}
          sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none", whiteSpace: "nowrap" }}
        >
          去分享笔记
        </Button>
      </Paper>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="搜索主题、读者昵称、经文或感悟..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 1.5,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2.5,
            },
          }}
        />

        {/* Tags */}
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
          <Chip
            label="全部主题"
            size="small"
            color={selectedTag === "all" ? "primary" : "default"}
            variant={selectedTag === "all" ? "filled" : "outlined"}
            onClick={() => setSelectedTag("all")}
            sx={{ fontWeight: 600 }}
          />
          {allTags.map((t) => (
            <Chip
              key={t}
              label={`#${t}`}
              size="small"
              color={selectedTag === t ? "primary" : "default"}
              variant={selectedTag === t ? "filled" : "outlined"}
              onClick={() => setSelectedTag(selectedTag === t ? "all" : t)}
              sx={{ fontWeight: 600 }}
            />
          ))}
        </Box>
      </Box>

      {/* Content */}
      {loading ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <CircularProgress size={36} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            正在加载社区灵修分享...
          </Typography>
        </Box>
      ) : filteredNotes.length > 0 ? (
        <Grid container spacing={2.5}>
          {filteredNotes.map((note) => {
            const author = note.userDisplayName || "主内读者";
            const initial = author[0] || "主";
            const chapterHref = chapterPathWithVerse(note.chapterId, note.verseRange);
            const ref = `${formatBookName(note.bookName) || ""} 第${note.chapterNumber || 1}章${
              note.verseRange ? `:${note.verseRange}` : ""
            }`;

            return (
              <Grid item xs={12} key={note.id}>
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: 3,
                    overflow: "visible",
                    border: "1px solid",
                    borderColor: note.featured ? "primary.main" : "divider",
                    bgcolor: (theme) =>
                      note.featured
                        ? theme.palette.mode === "dark"
                          ? "rgba(25, 118, 210, 0.05)"
                          : "rgba(25, 118, 210, 0.02)"
                        : theme.palette.background.paper,
                    position: "relative",
                  }}
                >
                  {/* Top Bar: Author Avatar, Name, Reference, Actions */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1.5,
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        cursor: note.authorId ? "pointer" : "default",
                        transition: "opacity 0.15s ease",
                        "&:hover": note.authorId ? { opacity: 0.82 } : {},
                      }}
                      onClick={(e) => {
                        if (note.authorId) {
                          e.stopPropagation();
                          openUserCard(note.authorId, {
                            displayName: author,
                            photoURL: note.authorPhotoURL,
                          });
                        }
                      }}
                    >
                      <Avatar
                        src={note.authorPhotoURL}
                        sx={{
                          width: 32,
                          height: 32,
                          fontSize: "0.85rem",
                          bgcolor: "primary.main",
                          fontWeight: 700,
                          transition: "transform 0.15s ease",
                          "&:hover": { transform: "scale(1.1)" },
                        }}
                      >
                        {initial}
                      </Avatar>
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 800,
                              "&:hover": { textDecoration: note.authorId ? "underline" : "none" },
                            }}
                          >
                            {author}
                          </Typography>
                          {note.featured && (
                            <Chip
                              icon={<StarIcon sx={{ fontSize: "14px !important" }} />}
                              label="精选"
                              size="small"
                              color="primary"
                              sx={{ height: 18, fontSize: "0.65rem", fontWeight: 700 }}
                            />
                          )}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {note.updatedAtIso || note.createdAtIso
                            ? new Date(
                                note.updatedAtIso || note.createdAtIso
                              ).toLocaleDateString()
                            : "近期"}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <Chip
                        label={ref}
                        size="small"
                        color="primary"
                        variant="outlined"
                        onClick={() => navigate(chapterHref)}
                        sx={{ fontWeight: 700, cursor: "pointer" }}
                      />

                      {note.authorId && (
                        <Tooltip title="与作者私信交流 / 加好友">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() =>
                              openChatWithUser(
                                {
                                  uid: note.authorId,
                                  displayName: note.userDisplayName,
                                  photoURL: note.authorPhotoURL,
                                },
                                {
                                  verseQuote: `${note.bookName || ""} ${note.chapterNumber || 1}${
                                    note.verseRange ? `:${note.verseRange}` : ""
                                  }`,
                                }
                              )
                            }
                          >
                            <ChatIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      <Tooltip title="复制笔记内容">
                        <IconButton
                          size="small"
                          onClick={() => handleCopyNote(note)}
                          color="default"
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="分享卡片链接">
                        <IconButton
                          size="small"
                          onClick={() => handleCopyLink(note)}
                          color="primary"
                        >
                          <ShareIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="举报不当内容">
                        <IconButton
                          size="small"
                          onClick={handleReport}
                          color="default"
                          sx={{ opacity: 0.6 }}
                        >
                          <FlagOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Title */}
                  <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
                    {note.title}
                  </Typography>

                  {/* Quoted Verse */}
                  {note.verseText && (
                    <Box
                      sx={{
                        p: 1.5,
                        mb: 1.5,
                        borderRadius: 2,
                        bgcolor: (theme) =>
                          theme.palette.mode === "dark"
                            ? "rgba(255,255,255,0.03)"
                            : "rgba(0,0,0,0.02)",
                        borderLeft: "3px solid",
                        borderLeftColor: "primary.main",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontStyle: "italic",
                          color: "text.secondary",
                          lineHeight: 1.6,
                        }}
                      >
                        {cleanVerseQuoteText(note.verseText)}
                      </Typography>
                    </Box>
                  )}

                  {/* Content */}
                  <Typography
                    variant="body1"
                    sx={{
                      lineHeight: 1.7,
                      whiteSpace: "pre-line",
                      color: "text.primary",
                      mb: 2,
                    }}
                  >
                    {note.content}
                  </Typography>

                  {/* Bottom: Tags & Open Bible Link */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {(note.tags || []).map((t) => (
                        <Chip
                          key={t}
                          label={`#${t}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: "0.7rem", height: 20 }}
                        />
                      ))}
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                        minWidth: 0,
                        justifyContent: "flex-end",
                      }}
                    >
                      {note.authorId && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ChatIcon fontSize="small" />}
                          onClick={() =>
                            openUserCard(note.authorId, {
                              displayName: author,
                              photoURL: note.authorPhotoURL,
                            })
                          }
                          sx={{
                            fontWeight: 700,
                            borderRadius: 2,
                            textTransform: "none",
                            fontSize: "0.75rem",
                            py: 0.3,
                            px: 1,
                            flexShrink: 0,
                          }}
                        >
                          与作者交流
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="text"
                        endIcon={<AutoStoriesIcon fontSize="small" />}
                        onClick={() => navigate(chapterHref)}
                        sx={{
                          fontWeight: 700,
                          px: 0.75,
                          py: 0.25,
                          mr: 0.25,
                          textTransform: "none",
                          flexShrink: 0,
                          overflow: "visible",
                          whiteSpace: "nowrap",
                          "& .MuiButton-endIcon": { mr: 0, ml: 0.5 },
                        }}
                      >
                        在圣经中阅读本章
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Paper
          elevation={0}
          sx={{
            textAlign: "center",
            py: 8,
            borderRadius: 3,
            border: "1px dashed",
            borderColor: "divider",
          }}
        >
          <GroupsIcon sx={{ fontSize: 48, opacity: 0.4, mb: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            暂无社区分享
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            成为第一个分享灵修心得的读者吧！
          </Typography>
          <Button
            variant="contained"
            size="small"
            onClick={() => navigate("/notes")}
            sx={{ borderRadius: 2, fontWeight: 700, textTransform: "none" }}
          >
            前往我的笔记分享
          </Button>
        </Paper>
      )}

      {/* Toast Notification */}
      <Snackbar
        open={Boolean(toastMsg)}
        autoHideDuration={3000}
        onClose={() => setToastMsg("")}
        message={toastMsg}
      />
    </Container>
  );
};

export default CommunityNotesPage;
